import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap, useMapEvent, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Truck,
  Package,
  RefreshCw,
  Wand2,
  MapPin,
  Loader2,
  CheckCircle2,
  Navigation,
  XCircle,
  Maximize2,
  Minimize2,
  LocateFixed,
  Map as MapIcon,
  Layers,
  Sparkles,
  Hand,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { formatTime } from '../lib/dateFormat';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import FleetSavingsCard from '../components/FleetSavingsCard';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import Rial from '../components/Rial';
import { orderPartyPrimary, orderPartyContact } from '../lib/orderParty';

// Available basemaps. Esri's World Imagery is free to use without an API
// key and gives high-resolution satellite globally (incl. Oman). A
// "hybrid" mode could overlay road labels but the typical use here is
// confirming a delivery location visually — streets mode is one tap away.
const BASEMAPS = {
  streets: {
    label: 'Streets',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  },
  satellite: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    maxZoom: 19,
  },
};

// Resolve a backend image URL (relative `/api/images/...`) to an absolute
// URL the Leaflet div renderer can fetch. Local /static or absolute URLs
// are returned unchanged. Same logic as ImageUpload.resolveImageUrl but
// kept inline so the icon factory has zero React-tree dependencies.
const resolveDriverPhoto = (raw) => {
  if (!raw) return '';
  if (/^(https?:|data:)/i.test(raw)) return raw;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}${raw.startsWith('/') ? '' : '/'}${raw}`;
};

const initialsOf = (name = '') => {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || 'D';
};

const escapeHtml = (s = '') =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// --- Custom icons (no external image deps) ---
//
// Zoom scaling: Leaflet zoom levels we care about:
//   8-10  → city / region view → tiny markers (avoid clutter)
//   11-13 → mid zoom → medium markers
//   14+   → street zoom → full-size markers with vehicle plate
//
// The factory takes a `scale` (1.0 = full size, 0.55 = compact) so callers
// can re-derive icons whenever the map zoom changes. We round sizes to
// integers — Leaflet's positioning math gets weird with fractional pixels.
const driverIcon = (driver, scale = 1) => {
  const online = !!driver.online;
  const ringColor = online ? '#10b981' : '#94a3b8';
  const photo = resolveDriverPhoto(driver.photo);
  const initials = escapeHtml(initialsOf(driver.name));
  const vehicle = driver.vehicle ? escapeHtml(driver.vehicle).slice(0, 16) : '';
  // Hide the plate when the marker is small — it becomes unreadable and
  // adds visual noise to a zoomed-out fleet view.
  const showPlate = vehicle && scale >= 0.85;

  const ringSize = Math.round(40 * scale);
  const photoSize = Math.round(32 * scale);
  const fontSize = Math.max(9, Math.round(12 * scale));
  const plateFont = Math.max(8, Math.round(9 * scale));
  const platePadV = Math.max(1, Math.round(1 * scale));
  const platePadH = Math.max(3, Math.round(6 * scale));
  const plateHeight = showPlate ? Math.round(16 * scale) + 4 : 0;
  const totalH = ringSize + plateHeight;

  const photoHtml = photo
    ? `<img src="${escapeHtml(photo)}"
            alt=""
            style="width:${photoSize}px;height:${photoSize}px;border-radius:50%;object-fit:cover;display:block;"
            onerror="this.style.display='none';this.parentNode.querySelector('.drv-fallback').style.display='flex';"/>
       <div class="drv-fallback" style="display:none;width:${photoSize}px;height:${photoSize}px;border-radius:50%;background:#0F172A;color:#fff;font-weight:700;font-size:${fontSize}px;align-items:center;justify-content:center;font-family:sans-serif;">${initials}</div>`
    : `<div style="width:${photoSize}px;height:${photoSize}px;border-radius:50%;background:#0F172A;color:#fff;font-weight:700;font-size:${fontSize}px;display:flex;align-items:center;justify-content:center;font-family:sans-serif;">${initials}</div>`;

  return L.divIcon({
    className: '',
    iconSize: [ringSize, totalH + 2],
    iconAnchor: [Math.round(ringSize / 2), totalH],
    html: `
      <div style="position:relative;width:${ringSize}px;display:flex;flex-direction:column;align-items:center;">
        <div style="
          width:${ringSize}px;height:${ringSize}px;border-radius:50%;
          background:${ringColor};
          padding:${Math.max(2, Math.round(4 * scale))}px;box-sizing:border-box;
          box-shadow:0 2px 6px rgba(0,0,0,0.35);
          display:flex;align-items:center;justify-content:center;
        ">
          ${photoHtml}
        </div>
        ${showPlate ? `
          <div style="
            margin-top:3px;
            padding:${platePadV}px ${platePadH}px;
            background:#0F172A;color:#fff;
            font-family:'SF Mono','Menlo','Consolas',monospace;
            font-size:${plateFont}px;font-weight:700;letter-spacing:0.5px;
            border-radius:3px;
            border:1px solid #fff;
            white-space:nowrap;
            box-shadow:0 1px 3px rgba(0,0,0,0.35);
            text-transform:uppercase;
          ">${vehicle}</div>
        ` : ''}
      </div>`,
  });
};

// Map a Leaflet zoom level to a marker scale factor.
//   ≤ 10  → 0.55 (city-wide, keep markers tiny)
//   13    → 0.80
//   ≥ 15  → 1.0  (street zoom, full size with plate)
const zoomToScale = (zoom) => {
  if (zoom <= 10) return 0.55;
  if (zoom >= 15) return 1.0;
  // Linear interpolation between 10 and 15
  return 0.55 + ((zoom - 10) / 5) * (1.0 - 0.55);
};

// Friendly "5 min", "1 h 12 min", "2 days" style from a timestamp.
const humanizeAgo = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (seconds < 30) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const m = minutes - hours * 60;
    return m ? `${hours}h ${m}m ago` : `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

// Tiny child component that subscribes to map zoom changes and pushes the
// current zoom up via callback. Re-renders only when the zoom actually
// changes — Leaflet fires `zoomend` after every pinch/scroll.
const ZoomTracker = ({ onZoom }) => {
  useMapEvent('zoomend', (e) => onZoom(e.target.getZoom()));
  return null;
};

const orderIcon = (status) => {
  const c =
    status === 'out_for_delivery' ? '#a855f7' :
    status === 'picked_up' ? '#f59e0b' :
    status === 'assigned' ? '#3b82f6' :
    '#ef4444'; // new / confirmed = urgent red
  return L.divIcon({
    className: '',
    iconSize: [28, 36],
    iconAnchor: [14, 34],
    html: `
      <div style="position:relative;width:28px;height:36px;">
        <svg viewBox="0 0 28 36" width="28" height="36">
          <path d="M14 0 C6 0 0 6 0 14 c0 10 14 22 14 22 s14-12 14-22 c0-8-6-14-14-14z"
            fill="${c}" stroke="#fff" stroke-width="2"/>
        </svg>
        <div style="position:absolute;top:4px;left:0;right:0;text-align:center;color:#fff;font-size:12px;">📦</div>
      </div>`,
  });
};

const STATUS_LABEL = {
  new: 'New',
  confirmed: 'Confirmed',
  assigned: 'Assigned',
  picked_up: 'Picked up',
  out_for_delivery: 'En route',
  delivered: 'Delivered',
  failed: 'Failed',
};
const STATUS_PILL = {
  new: 'bg-red-100 text-red-700',
  confirmed: 'bg-orange-100 text-orange-700',
  assigned: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-amber-100 text-amber-700',
  out_for_delivery: 'bg-purple-100 text-purple-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-neutral-200 text-neutral-700',
};

// Default map center — Muscat, Oman
const DEFAULT_CENTER = [23.588, 58.408];

const FitBounds = ({ points, enabled, fitNonce }) => {
  const map = useMap();
  useEffect(() => {
    if (!enabled) return;
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
    }
  // Including fitNonce lets a "Recenter" button trigger a fresh fit even
  // when `points` is referentially identical to the previous render.
  }, [points, map, enabled, fitNonce]);
  return null;
};

/**
 * Watches for user-initiated map gestures (zoom, pan, scroll) and reports
 * them back to the page via `onUserInteract`. We use this to switch the
 * dispatch map out of auto-fit mode as soon as the admin starts driving
 * the camera manually — otherwise the every-5-second data refresh keeps
 * snapping their view back to fitBounds.
 */
const MapInteractionWatcher = ({ onUserInteract }) => {
  useMapEvents({
    // dragstart fires on touch-pan + mouse-drag; zoomstart fires on +/- + pinch + wheel.
    // We treat any of these as "the admin has taken over".
    dragstart: () => onUserInteract(),
    zoomstart: (e) => {
      // The programmatic fitBounds call also raises zoomstart. Distinguish
      // by checking if the event came from a user gesture — Leaflet adds
      // _animateToCenter only on flyTo / fitBounds; for user gestures the
      // `originalEvent` is present.
      if (e?.originalEvent || e?.target?._animatingZoom === false) onUserInteract();
    },
  });
  return null;
};

const AdminDispatch = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const { can } = useAdminPermissions();
  // Only super-admins / `settings.edit` holders can toggle the master
  // auto-assign switch. Everyone else with dispatch access can still
  // see the current mode (so they understand why incoming orders sit
  // unassigned) but the Switch is rendered read-only.
  const canEditAutoAssign = can('settings.edit');
  const [data, setData] = useState({ orders: [], drivers: [] });
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [assigning, setAssigning] = useState(false);
  // Tracks the live Leaflet zoom level so each Marker re-renders with the
  // correct icon size. Default 12 matches the initial MapContainer zoom.
  const [zoom, setZoom] = useState(12);
  // Auto-fit ON until the admin manually pans/zooms — then it stays OFF
  // so the every-5s data refresh doesn't snap their view back. A "Recenter"
  // button re-enables auto-fit for a one-shot recenter (we bump fitNonce).
  const [autoFit, setAutoFit] = useState(true);
  const [fitNonce, setFitNonce] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  // Basemap choice persisted across sessions — once an admin picks satellite
  // they usually want it on every visit.
  const [basemap, setBasemap] = useState(() => {
    try { return localStorage.getItem('mr_dispatch_basemap') || 'streets'; } catch { return 'streets'; }
  });

  // Master auto-assign switch. Pulled from /api/settings on mount and
  // PUT back on every toggle. We optimistically flip the local state so
  // the Switch animation feels instant — reverted on save failure.
  const [autoAssign, setAutoAssign] = useState(true);
  const [autoAssignSaving, setAutoAssignSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/dispatch`, { headers: authHeaders });
      setData(d);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
    // Auto-refresh disabled per user request — Live polling reserved for
    // the public Live Stock report only. Use the manual refresh button.
  }, [load]);

  // Fetch the master auto-assign flag once on mount. The endpoint is
  // public, so we don't need auth headers — but we'll PUT through the
  // authed /admin/settings route below when the admin toggles it.
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/settings`)
      .then(({ data: s }) => {
        if (alive) setAutoAssign(s?.auto_assign_drivers !== false);
      })
      .catch(() => { /* keep default */ });
    return () => { alive = false; };
  }, []);

  const toggleAutoAssign = async (next) => {
    if (!canEditAutoAssign || autoAssignSaving) return;
    setAutoAssign(next);  // optimistic
    setAutoAssignSaving(true);
    try {
      // Fetch the FULL settings doc first — the PUT replaces every field
      // (it isn't a PATCH), so we must round-trip everything else
      // untouched. /settings is public and contains the full doc minus
      // any admin-only secrets (there are none for SiteSettings).
      const { data: current } = await axios.get(`${API}/settings`);
      await axios.put(
        `${API}/admin/settings`,
        { ...current, auto_assign_drivers: next },
        { headers: authHeaders },
      );
      toast({
        title: next ? 'Auto-assign enabled' : 'Auto-assign disabled',
        description: next
          ? 'New orders will be auto-routed to the nearest available driver.'
          : 'New orders will stay in the dispatch queue for manual assignment.',
      });
    } catch (err) {
      setAutoAssign(!next);  // revert
      toast({
        title: 'Failed to save',
        description: err?.response?.data?.detail || 'Try again',
      });
    } finally {
      setAutoAssignSaving(false);
    }
  };

  // Persist basemap choice — never throws (private mode blocks localStorage).
  useEffect(() => {
    try { localStorage.setItem('mr_dispatch_basemap', basemap); } catch (_) { /* ignore */ }
  }, [basemap]);

  // Escape exits fullscreen and re-runs Leaflet's invalidateSize on toggle so
  // the map fills the new viewport without dead grey areas. We invalidate
  // shortly after the state change so the DOM has resized first.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && fullscreen) setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  useEffect(() => {
    // Leaflet caches the container size; without invalidateSize the tiles
    // don't repaint when we toggle fullscreen on/off.
    const t = setTimeout(() => {
      const el = document.querySelector('.leaflet-container');
      if (el && el._leaflet_id && window.L) {
        // Walk all live maps via L.DomUtil registry; simpler is to fire
        // a window resize which Leaflet listens for natively.
        window.dispatchEvent(new Event('resize'));
      } else {
        window.dispatchEvent(new Event('resize'));
      }
    }, 220); // match CSS transition delay
    return () => clearTimeout(t);
  }, [fullscreen]);

  const unassignedOrders = data.orders.filter((o) => !o.driver_id && ['new', 'confirmed'].includes(o.status));
  const inFlight = data.orders.filter((o) => o.driver_id && ['assigned', 'picked_up', 'out_for_delivery'].includes(o.status));

  const points = useMemo(() => {
    const arr = [];
    data.drivers.forEach((d) => d.last_lat && d.last_lng && arr.push([d.last_lat, d.last_lng]));
    data.orders.forEach((o) => o.location_lat && o.location_lng && arr.push([o.location_lat, o.location_lng]));
    return arr;
  }, [data]);

  const suggestForOrder = async (order) => {
    setAssigning(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/orders/${order.id}/suggest-driver`, { headers: authHeaders });
      if (!res.driver) {
        toast({ title: 'No drivers available', description: res.reason });
        return;
      }
      const ok = window.confirm(
        `Assign ${res.driver.name} to order ${order.order_number}?\n\nReason: ${res.reason}`,
      );
      if (!ok) return;
      await assignOrder(order, res.driver.id);
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setAssigning(false);
    }
  };

  const assignOrder = async (order, driverId) => {
    try {
      await axios.post(
        `${API}/admin/orders/${order.id}/assign`,
        { driver_id: driverId },
        { headers: authHeaders },
      );
      toast({ title: 'Driver assigned' });
      setSelectedOrder(null);
      await load();
    } catch (err) {
      toast({ title: 'Assign failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">Dispatch</h1>
            <p className="text-neutral-500 mt-1">Live fleet, active orders, and one-tap driver assignment.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Auto-assign master switch — when ON, every new order is
                routed to the nearest available driver. When OFF, every
                order sits in "Awaiting dispatch" until an admin picks. */}
            <div
              className={`flex items-center gap-2.5 px-3 py-2 rounded-full border ${
                autoAssign
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-amber-200 bg-amber-50'
              }`}
              data-testid="auto-assign-toggle-wrap"
            >
              {autoAssign ? (
                <Sparkles className="w-4 h-4 text-emerald-600" />
              ) : (
                <Hand className="w-4 h-4 text-amber-600" />
              )}
              <span className={`text-xs font-bold uppercase tracking-wider ${
                autoAssign ? 'text-emerald-700' : 'text-amber-700'
              }`}>
                {autoAssign ? 'Auto-assign' : 'Manual assign'}
              </span>
              <Switch
                checked={autoAssign}
                onCheckedChange={toggleAutoAssign}
                disabled={!canEditAutoAssign || autoAssignSaving}
                className="data-[state=checked]:bg-emerald-500"
                data-testid="auto-assign-toggle"
              />
              {!canEditAutoAssign && (
                <span className="text-[10px] italic text-neutral-400">
                  (view-only)
                </span>
              )}
            </div>
            <Button onClick={load} variant="ghost" className="rounded-full" data-testid="dispatch-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Tile label="Online drivers" value={data.drivers.filter((d) => d.online).length} accent="bg-emerald-500" />
          <Tile label="Unassigned" value={unassignedOrders.length} accent="bg-red-500" />
          <Tile label="In transit" value={inFlight.length} accent="bg-purple-500" />
          <Tile label="Total drivers" value={data.drivers.length} accent="bg-[#0078D4]" />
        </div>

        {/* Main layout: list + map */}
        <div className="grid lg:grid-cols-[420px_1fr] gap-5">
          {/* Unassigned list */}
          <div className="space-y-3">
            <FleetSavingsCard apiBase={API} authHeaders={authHeaders} />
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-bold text-[#0F172A]">Awaiting dispatch</h2>
                <span className="text-xs text-neutral-500">{unassignedOrders.length}</span>
              </div>
              {unassignedOrders.length === 0 ? (
                <p className="text-sm text-neutral-500">All caught up — no unassigned orders.</p>
              ) : (
                <div className="space-y-2" data-testid="unassigned-orders">
                  {unassignedOrders.map((o) => (
                    <div
                      key={o.id}
                      className={`rounded-xl border p-3 cursor-pointer transition ${
                        selectedOrder?.id === o.id
                          ? 'border-[#0078D4] bg-[#EFF7FF]'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                      onClick={() => setSelectedOrder(o)}
                      data-testid={`dispatch-order-${o.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">
                          {o.order_number}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_PILL[o.status]}`}>
                          {STATUS_LABEL[o.status]}
                        </span>
                      </div>
                      <div className="font-semibold text-sm text-[#0F172A] truncate" data-testid={`dispatch-order-party-${o.id}`}>
                        {orderPartyPrimary(o)}
                        {orderPartyContact(o) && (
                          <span className="ml-1 text-[11px] font-normal text-neutral-500">· {orderPartyContact(o)}</span>
                        )}
                      </div>
                      <div className="text-xs text-neutral-500 truncate">
                        {o.location_address || (<>{o.items?.length || 0} items · <Rial /> {(o.total || 0).toFixed(3)}</>)}
                      </div>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          suggestForOrder(o);
                        }}
                        disabled={assigning}
                        className="mt-2 rounded-full bg-[#0F172A] hover:bg-black text-white text-xs"
                        data-testid={`suggest-driver-${o.id}`}
                      >
                        <Wand2 className="w-3.5 h-3.5 mr-1" /> Auto-assign nearest
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* In-flight list */}
            <div className="bg-white rounded-2xl border border-neutral-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-bold text-[#0F172A]">In transit</h2>
                <span className="text-xs text-neutral-500">{inFlight.length}</span>
              </div>
              {inFlight.length === 0 ? (
                <p className="text-sm text-neutral-500">No active deliveries.</p>
              ) : (
                <div className="space-y-2" data-testid="in-flight-orders">
                  {inFlight.map((o) => {
                    const driver = data.drivers.find((d) => d.id === o.driver_id);
                    return (
                      <div key={o.id} className="rounded-xl border border-neutral-200 p-3" data-testid={`flight-order-${o.id}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">
                            {o.order_number}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_PILL[o.status]}`}>
                            {STATUS_LABEL[o.status]}
                          </span>
                        </div>
                        <div className="text-sm font-semibold text-[#0F172A] truncate" data-testid={`dispatch-assigned-order-party-${o.id}`}>
                          {orderPartyPrimary(o)}
                          {orderPartyContact(o) && (
                            <span className="ml-1 text-[11px] font-normal text-neutral-500">· {orderPartyContact(o)}</span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-600 flex items-center gap-1.5 mt-1">
                          <Truck className="w-3.5 h-3.5" />
                          {driver ? driver.name : 'Unknown driver'}
                          {driver?.online && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 ml-1" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div
            className={
              fullscreen
                ? 'fixed inset-0 z-[9999] bg-white'
                : 'bg-white rounded-2xl border border-neutral-200 overflow-hidden h-[70vh] min-h-[500px] relative'
            }
            data-testid="map-shell"
          >
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={11}
              scrollWheelZoom={true}
              className="h-full w-full"
              data-testid="dispatch-map"
            >
              {(() => {
                const bm = BASEMAPS[basemap] || BASEMAPS.streets;
                return (
                  <TileLayer
                    key={basemap}
                    url={bm.url}
                    attribution={bm.attribution}
                    maxZoom={bm.maxZoom}
                  />
                );
              })()}
              <FitBounds points={points} enabled={autoFit} fitNonce={fitNonce} />
              <MapInteractionWatcher onUserInteract={() => setAutoFit(false)} />
              <ZoomTracker onZoom={setZoom} />

              {/* Floating map controls — Recenter + Fullscreen.
                  Sit on top-right just below the OSM zoom buttons so they
                  don't compete with Leaflet's own controls. */}

              {data.drivers.map((d) => {
                if (!d.last_lat || !d.last_lng) return null;
                const scale = zoomToScale(zoom);
                const ago = humanizeAgo(d.last_seen);
                const tooltipText = d.online
                  ? `${d.name} · online ${ago}`
                  : `${d.name} · offline (${ago || 'never seen'})`;
                return (
                  <Marker
                    key={`d-${d.id}`}
                    position={[d.last_lat, d.last_lng]}
                    icon={driverIcon(d, scale)}
                  >
                    <Tooltip direction="top" offset={[0, -8]} opacity={0.95} sticky>
                      <div style={{ fontFamily: 'sans-serif', fontSize: 11, lineHeight: 1.35 }}>
                        {tooltipText}
                      </div>
                    </Tooltip>
                    <Popup minWidth={220}>
                      <div className="text-sm font-sans" data-testid={`driver-popup-${d.id}`}>
                        <div className="flex items-start gap-2.5 pb-2 border-b border-neutral-200">
                          <div
                            className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold"
                            style={{
                              background: d.online ? '#10b981' : '#94a3b8',
                              padding: 2,
                            }}
                          >
                            {d.photo ? (
                              <img
                                src={resolveDriverPhoto(d.photo)}
                                alt=""
                                className="w-full h-full rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <span className="text-xs">{initialsOf(d.name)}</span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-[#0F172A] truncate">{d.name}</div>
                            <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                              <span
                                className={`inline-block w-1.5 h-1.5 rounded-full ${
                                  d.online ? 'bg-emerald-500' : 'bg-amber-500'
                                }`}
                              />
                              {d.online ? `Online · ${ago}` : ago ? `Last seen ${ago}` : 'Never seen'}
                            </div>
                          </div>
                        </div>
                        <dl className="mt-2 text-xs space-y-1">
                          <div className="flex items-center justify-between gap-3">
                            <dt className="text-neutral-500">Vehicle</dt>
                            <dd className="font-mono font-semibold text-[#0F172A] truncate">
                              {d.vehicle || '—'}
                            </dd>
                          </div>
                          {d.phone && (
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-neutral-500">Phone</dt>
                              <dd className="font-mono">
                                <a
                                  href={`tel:${d.phone}`}
                                  className="text-[#0078D4] hover:underline font-semibold"
                                >
                                  {d.phone}
                                </a>
                              </dd>
                            </div>
                          )}
                          {d.email && (
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-neutral-500">Email</dt>
                              <dd className="truncate max-w-[140px]" title={d.email}>
                                <a
                                  href={`mailto:${d.email}`}
                                  className="text-[#0078D4] hover:underline"
                                >
                                  {d.email}
                                </a>
                              </dd>
                            </div>
                          )}
                          {Array.isArray(d.categories) && d.categories.length > 0 && (
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-neutral-500">Divisions</dt>
                              <dd className="text-right text-[10px] uppercase tracking-wide font-semibold text-neutral-700 truncate max-w-[140px]">
                                {d.categories.join(', ')}
                              </dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {data.orders.map(
                (o) =>
                  o.location_lat &&
                  o.location_lng && (
                    <Marker
                      key={`o-${o.id}`}
                      position={[o.location_lat, o.location_lng]}
                      icon={orderIcon(o.status)}
                      eventHandlers={{ click: () => setSelectedOrder(o) }}
                    >
                      <Popup>
                        <div className="text-sm font-sans space-y-1 min-w-[180px]">
                          <div className="font-bold text-[#0F172A]">{orderPartyPrimary(o)}</div>
                          {orderPartyContact(o) && (
                            <div className="text-[11px] text-neutral-600">Contact: {orderPartyContact(o)}</div>
                          )}
                          <div className="text-xs text-neutral-500">{o.order_number}</div>
                          <div className="text-xs">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_PILL[o.status]}`}>
                              {STATUS_LABEL[o.status]}
                            </span>
                          </div>
                          {!o.driver_id && (
                            <button
                              onClick={() => suggestForOrder(o)}
                              className="text-xs font-bold text-[#0078D4] underline"
                            >
                              Auto-assign driver
                            </button>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ),
              )}
            </MapContainer>

            {/* Floating action buttons — placed top-right under Leaflet zoom controls.
                z-[500] keeps them above tiles but below modal overlays. */}
            <div
              className="absolute top-3 right-3 z-[500] flex flex-col gap-1.5"
              data-testid="map-controls"
            >
              <button
                type="button"
                onClick={() => setBasemap((b) => (b === 'streets' ? 'satellite' : 'streets'))}
                title={basemap === 'satellite' ? 'Switch to streets' : 'Switch to satellite'}
                className="w-9 h-9 rounded-lg bg-white shadow-md border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center text-[#0F172A]"
                data-testid="map-basemap-toggle"
              >
                {basemap === 'satellite' ? <MapIcon className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAutoFit(true);
                  setFitNonce((n) => n + 1);
                }}
                title="Recenter map on all markers"
                className="w-9 h-9 rounded-lg bg-white shadow-md border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center text-[#0F172A]"
                data-testid="map-recenter-btn"
              >
                <LocateFixed className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setFullscreen((v) => !v)}
                title={fullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen map'}
                className="w-9 h-9 rounded-lg bg-white shadow-md border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center text-[#0F172A]"
                data-testid="map-fullscreen-btn"
              >
                {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>

            {/* Auto-fit suspended hint — appears once the admin starts driving the
                map manually so they know why subsequent data refreshes don't
                snap their view back. Click to dismiss + recenter. */}
            {!autoFit && (
              <button
                type="button"
                onClick={() => {
                  setAutoFit(true);
                  setFitNonce((n) => n + 1);
                }}
                className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[500] px-3 py-1.5 rounded-full bg-[#0F172A] text-white text-[11px] font-semibold shadow-lg flex items-center gap-1.5 hover:bg-black"
                data-testid="map-autofit-banner"
              >
                <LocateFixed className="w-3.5 h-3.5" />
                Auto-fit paused · tap to recenter
              </button>
            )}

            {/* Selected order detail sheet */}
            {selectedOrder && (
              <div
                className="absolute top-4 right-4 w-[300px] bg-white rounded-xl shadow-2xl border border-neutral-200 p-4 z-[1000]"
                data-testid="dispatch-order-detail"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-neutral-500">
                    {selectedOrder.order_number}
                  </span>
                  <button onClick={() => setSelectedOrder(null)} className="text-neutral-400 hover:text-neutral-700 text-xl leading-none">×</button>
                </div>
                <div className="font-display text-lg font-bold text-[#0F172A]" data-testid={`dispatch-detail-party-${selectedOrder.id}`}>
                  {orderPartyPrimary(selectedOrder)}
                </div>
                {orderPartyContact(selectedOrder) && (
                  <div className="text-[11px] text-neutral-500">Contact: {orderPartyContact(selectedOrder)}</div>
                )}
                <div className="text-xs text-neutral-500 mt-0.5">{selectedOrder.customer_phone}</div>
                {selectedOrder.location_address && (
                  <div className="text-xs text-neutral-600 mt-2 flex items-start gap-1">
                    <MapPin className="w-3 h-3 mt-0.5 text-[#0078D4] flex-shrink-0" />
                    <span>{selectedOrder.location_address}</span>
                  </div>
                )}
                <div className="mt-3 text-xs">Items: {selectedOrder.items?.length || 0} · <Rial /> {(selectedOrder.total || 0).toFixed(3)}</div>
                <div className="mt-3 space-y-2">
                  {!selectedOrder.driver_id && (
                    <>
                      <Button
                        onClick={() => suggestForOrder(selectedOrder)}
                        disabled={assigning}
                        className="w-full rounded-full bg-[#0F172A] hover:bg-black text-white text-xs"
                      >
                        {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                        Auto-assign nearest
                      </Button>
                      <select
                        onChange={(e) => e.target.value && assignOrder(selectedOrder, e.target.value)}
                        className="w-full rounded-full border border-neutral-200 px-3 py-2 text-xs"
                        defaultValue=""
                        data-testid="manual-assign-select"
                      >
                        <option value="" disabled>Assign manually…</option>
                        {data.drivers
                          .filter((d) => d.active)
                          .map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name} {d.online ? '· online' : ''}
                            </option>
                          ))}
                      </select>
                    </>
                  )}
                  <a
                    href={`/track/${selectedOrder.order_number}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-center text-xs font-semibold text-[#0078D4] hover:underline"
                  >
                    Open public tracker →
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const Tile = ({ label, value, accent }) => (
  <div className="bg-white rounded-2xl border border-neutral-200 p-4 flex items-center gap-3">
    <span className={`w-2 h-10 rounded-full ${accent}`} />
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-2xl font-extrabold text-[#0F172A] font-display leading-none mt-1">{value}</div>
    </div>
  </div>
);

export default AdminDispatch;
